import type { DeviceInputOperationMode, DeviceOutputOperationMode } from "@irriga-mais/common";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { WATER_PUMP_TYPE_LABELS, WATER_PUMP_TYPE_VALUES, type WaterPumpType } from "@/api/model/water-pump";
import type { AUTDevice, AUTWaterPump } from "@/api/queries/account";
import { Modal, useToast } from "@/components";
import { ControllerConfigurationPanel, type ControllerConfigValue } from "@/components/ControllerConfigurationPanel";
import { DeviceActivationPanel } from "@/components/DeviceActivationPanel";
import { ModalFooter } from "@/components/ui/ModalFooter";
import { selectedPropertyIdAtom, waterPumpsAtom } from "@/store";
import {
  createVirtualDeviceConfigurationAtom,
  createWaterPumpAtom,
  updateVirtualDeviceConfigurationAtom,
  updateWaterPumpAtom,
} from "@/store/crud";
import {
  propertyDevicesAtom,
  virtualDeviceConfigurationAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import { isMeshDevice } from "@/utils/mesh-device-utils";

interface PumpDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  pump: AUTWaterPump | null;
  mode: "create" | "edit";
  showSerialNumberInput?: boolean;
}

function PumpDetailModal({ isOpen, onClose, pump, mode, showSerialNumberInput = false }: PumpDetailModalProps) {
  const createWaterPump = useSetAtom(createWaterPumpAtom);
  const updateWaterPump = useSetAtom(updateWaterPumpAtom);
  const selectedPropertyId = useAtomValue(selectedPropertyIdAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const waterPumps = useAtomValue(waterPumpsAtom);
  const virtualDeviceConfigurations = useAtomValue(virtualDeviceConfigurationAtom);
  const waterPumpVirtualDeviceConfigMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);
  const createVirtualDeviceConfiguration = useSetAtom(createVirtualDeviceConfigurationAtom);
  const updateVirtualDeviceConfiguration = useSetAtom(updateVirtualDeviceConfigurationAtom);

  const { showWarning } = useToast();

  const currentWaterPumpVirtualDeviceConfig = useMemo(() => {
    return pump?.id ? waterPumpVirtualDeviceConfigMap.get(pump.id) : null;
  }, [waterPumpVirtualDeviceConfigMap, pump?.id]);

  const [formData, setFormData] = useState({
    label: "",
    identifier: "",
    pump_type: null as WaterPumpType | null,
    pump_model: "",
    water_pump_controller: null as string | null,
    water_pump_controller_turn_on_output: null as number | null,
    water_pump_controller_turn_off_output: null as number | null,
    water_pump_controller_monitoring_input: null as number | null,
    water_pump_controller_output_mode: "continuous" as DeviceOutputOperationMode,
    water_pump_controller_monitoring_input_mode: "digital" as DeviceInputOperationMode,
    flow_rate_lh: null as number | null,
  });

  useEffect(() => {
    if (pump && mode === "edit") {
      setFormData({
        label: pump.label,
        identifier: pump.identifier,
        pump_type: pump.pump_type,
        pump_model: pump.pump_model,
        water_pump_controller: currentWaterPumpVirtualDeviceConfig?.device?.id || null,
        water_pump_controller_turn_on_output:
          currentWaterPumpVirtualDeviceConfig?.turn_on_physical_output_index ?? null,
        water_pump_controller_turn_off_output:
          currentWaterPumpVirtualDeviceConfig?.turn_off_physical_output_index ?? null,
        water_pump_controller_monitoring_input:
          currentWaterPumpVirtualDeviceConfig?.monitoring_physical_input_index ?? null,
        water_pump_controller_output_mode: currentWaterPumpVirtualDeviceConfig?.mode || "continuous",
        water_pump_controller_monitoring_input_mode:
          currentWaterPumpVirtualDeviceConfig?.monitoring_input_mode || "digital",
        flow_rate_lh: pump.flow_rate_lh || null,
      });
    } else if (mode === "create") {
      setFormData({
        label: "",
        identifier: "",
        pump_type: null,
        pump_model: "",
        flow_rate_lh: null,
        water_pump_controller_output_mode: "continuous",
        water_pump_controller_turn_on_output: null,
        water_pump_controller_turn_off_output: null,
        water_pump_controller_monitoring_input: null,
        water_pump_controller_monitoring_input_mode: "digital",
        water_pump_controller: null,
      });
    }
  }, [pump, mode, currentWaterPumpVirtualDeviceConfig]);

  // Get all mesh devices as available controllers for pumps
  const availableControllers = useMemo(() => {
    return propertyDevices.map((pd) => pd.device).filter((d) => isMeshDevice(d.model));
  }, [propertyDevices]);

  // Create controller config value for ControllerConfigurationPanel
  const controllerConfigValue = useMemo<ControllerConfigValue>(() => {
    return {
      controllerId: formData.water_pump_controller,
      turnOnOutputIndex: formData.water_pump_controller_turn_on_output,
      turnOffOutputIndex: formData.water_pump_controller_turn_off_output,
      monitoringInputIndex: formData.water_pump_controller_monitoring_input,
      outputMode: formData.water_pump_controller_output_mode,
      inputMode: formData.water_pump_controller_monitoring_input_mode,
    };
  }, [
    formData.water_pump_controller,
    formData.water_pump_controller_turn_on_output,
    formData.water_pump_controller_turn_off_output,
    formData.water_pump_controller_monitoring_input,
    formData.water_pump_controller_output_mode,
    formData.water_pump_controller_monitoring_input_mode,
  ]);

  // Handle controller config changes from ControllerConfigurationPanel
  const handleControllerConfigChange = useCallback((value: ControllerConfigValue) => {
    setFormData((prev) => ({
      ...prev,
      water_pump_controller: value.controllerId,
      water_pump_controller_turn_on_output: value.turnOnOutputIndex,
      water_pump_controller_turn_off_output: value.turnOffOutputIndex,
      water_pump_controller_monitoring_input: value.monitoringInputIndex,
      water_pump_controller_output_mode: value.outputMode,
      water_pump_controller_monitoring_input_mode: value.inputMode,
    }));
  }, []);

  // Additional mode constraints for WPC-PL50
  const getAdditionalModeConstraints = useCallback(
    (controller: AUTDevice, outputIndex: number | null) => {
      if (controller.model.id === "WPC-PL50") {
        if (formData.pump_type === "FERTIGATION") {
          return "WPC-PL50 em fertirrigação exige modo CONTINUOUS.";
        }
        if (formData.pump_type === "IRRIGATION") {
          // Check if there's a fertigation pump using the same controller
          const fertigationUsingController = Array.from(waterPumpVirtualDeviceConfigMap.values()).some(
            (vdc) =>
              vdc &&
              vdc.device.id === controller.id &&
              vdc.equipment_type === "pump" &&
              vdc.water_pump &&
              waterPumps.find((p) => p.id === vdc.water_pump)?.pump_type === "FERTIGATION"
          );

          if (fertigationUsingController) {
            return "WPC-PL50 compartilhado com fertirrigação exige modo CONTINUOUS.";
          }
        }
      }
      return null;
    },
    [formData.pump_type, waterPumpVirtualDeviceConfigMap, waterPumps]
  );


  // Handle pump type change - reset controller when type changes
  const handlePumpTypeChange = (newPumpType: WaterPumpType | null) => {
    setFormData((prev) => ({
      ...prev,
      pump_type: newPumpType,
    }));
  };

  const handleSave = async () => {
    if (!selectedPropertyId) {
      console.error("No property selected for the pump.");
      showWarning({
        message: "Nenhuma propriedade selecionada para a bomba.",
      });
      return;
    }

    // Guard: When configuring a fertigation with WPC-PL50, if controller is already
    // associated to an irrigation pump of mode PULSE, block the assignment
    // if (formData.pump_type === "FERTIGATION" && selectedController?.device?.model === "WPC-PL50") {
    //   const controllerId = selectedController.id;
    //   const irrigationPulseExists = waterPumps.some(
    //     (p) =>
    //       p.id !== pump?.id &&
    //       p.pump_type === "IRRIGATION" &&
    //       p.mode === "PULSE" &&
    //       p.water_pump_controller === controllerId
    //   );
    //   if (irrigationPulseExists) {
    //     showWarning({
    //       message:
    //         "Este WPC-PL50 está associado a uma bomba de irrigação em modo PULSE e não pode ser usado na fertirrigação.",
    //     });
    //     return;
    //   }
    // }
    const data = {
      flow_rate_lh: formData.flow_rate_lh || null,
      identifier: formData.identifier.trim() || formData.label.trim(),
      label: formData.label.trim() || formData.identifier.trim(),
      pump_type: formData.pump_type ?? undefined,
      property: selectedPropertyId,
      pump_model: formData.pump_model || undefined,
    };
    // TODO: Implement save logic with API call
    if (mode === "edit" && pump) {
      await updateWaterPump({
        id: pump.id,
        data, // Cast to avoid type mismatch with DirectusRelationFieldType
      });
      const hasVirtualConfigurationChanged =
        (!currentWaterPumpVirtualDeviceConfig && formData.water_pump_controller) ||
        (currentWaterPumpVirtualDeviceConfig && !formData.water_pump_controller) ||
        (currentWaterPumpVirtualDeviceConfig &&
          formData.water_pump_controller &&
          (currentWaterPumpVirtualDeviceConfig.device.id !== formData.water_pump_controller ||
            currentWaterPumpVirtualDeviceConfig.mode !== formData.water_pump_controller_output_mode ||
            currentWaterPumpVirtualDeviceConfig.turn_on_physical_output_index !==
              formData.water_pump_controller_turn_on_output ||
            currentWaterPumpVirtualDeviceConfig.turn_off_physical_output_index !==
              formData.water_pump_controller_turn_off_output ||
            currentWaterPumpVirtualDeviceConfig.monitoring_physical_input_index !==
              formData.water_pump_controller_monitoring_input));
      if (hasVirtualConfigurationChanged) {
        if (currentWaterPumpVirtualDeviceConfig) {
          await updateVirtualDeviceConfiguration({
            id: currentWaterPumpVirtualDeviceConfig.id,
            data: {
              end_date: new Date().toISOString(),
            },
          });
        }
        if (formData.water_pump_controller) {
          await createVirtualDeviceConfiguration({
            device: formData.water_pump_controller,
            mode: formData.water_pump_controller_output_mode,
            turn_on_physical_output_index: formData.water_pump_controller_turn_on_output!,
            turn_off_physical_output_index:
              formData.water_pump_controller_output_mode === "continuous"
                ? null
                : formData.water_pump_controller_turn_off_output!,
            monitoring_physical_input_index: formData.water_pump_controller_monitoring_input || null,
            monitoring_input_mode: formData.water_pump_controller_monitoring_input
              ? formData.water_pump_controller_monitoring_input_mode
              : null,
            equipment_type: "pump",
            water_pump: pump.id,
            start_date: new Date().toISOString(),
          });
        }
      }
    } else if (mode === "create") {
      // Create new water pump
      const newPump = await createWaterPump(data); // Cast to avoid type mismatch with DirectusRelationFieldType
      if (newPump.success && newPump.data && formData.water_pump_controller) {
        // TODO: Create virtual device configuration
        await createVirtualDeviceConfiguration({
          device: formData.water_pump_controller!,
          mode: formData.water_pump_controller_output_mode,
          turn_on_physical_output_index: formData.water_pump_controller_turn_on_output!,
          turn_off_physical_output_index:
            formData.water_pump_controller_output_mode === "continuous"
              ? null
              : formData.water_pump_controller_turn_off_output!,
          monitoring_physical_input_index: formData.water_pump_controller_monitoring_input || null,
          monitoring_input_mode: formData.water_pump_controller_monitoring_input
            ? formData.water_pump_controller_monitoring_input_mode
            : null,
          equipment_type: "pump",
          water_pump: newPump.data.id,
          start_date: new Date().toISOString(),
        });
      }
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === "create" ? "Nova Bomba" : "Editar Bomba"} size="md">
      <div className="space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
          <input
            type="text"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            className="placeholder:text-red-900! w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            placeholder="Nome da Bomba"
          />
        </div>

        {/* Water Pump Serial Number */}
        {showSerialNumberInput && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">S/N</label>
            <input
              type="text"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              placeholder="S/N XXXXXXX"
            />
          </div>
        )}

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
          <select
            value={formData.pump_type ?? ""}
            onChange={(e) => handlePumpTypeChange((e.target.value as WaterPumpType) || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          >
            <option value="">Selecione o tipo da bomba</option>
            {WATER_PUMP_TYPE_VALUES.map((type) => (
              <option key={type} value={type}>
                {WATER_PUMP_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Model */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
          <input
            type="text"
            value={formData.pump_model}
            onChange={(e) => setFormData({ ...formData, pump_model: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            placeholder="Modelo da bomba"
          />
        </div>

        {/* Flow Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vazão (L/h)</label>
          <input
            type="number"
            value={formData.flow_rate_lh || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                flow_rate_lh: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            placeholder="1000"
            min="0"
            step="0.1"
          />
          <p className="mt-1 text-xs text-gray-500">Taxa de fluxo da bomba em litros por hora</p>
        </div>

        {/* Controller Configuration */}
        <ControllerConfigurationPanel
          equipmentType="pump"
          currentConfig={currentWaterPumpVirtualDeviceConfig}
          value={controllerConfigValue}
          onChange={handleControllerConfigChange}
          availableControllers={availableControllers}
          disabled={!formData.pump_type}
          showModeSelection={true}
          showMonitoringInput={true}
          additionalModeConstraints={getAdditionalModeConstraints}
        />

        <div className="flex flex-col gap-0.5">
          {currentWaterPumpVirtualDeviceConfig && pump && (
            <DeviceActivationPanel
              elementType="pump"
              elementId={pump.id}
              elementControllerId={currentWaterPumpVirtualDeviceConfig.device.id}
              turnOnDurationMinutes={1}
              title={`Testar Acionamento (${currentWaterPumpVirtualDeviceConfig.device.identifier} / S-${currentWaterPumpVirtualDeviceConfig.turn_on_physical_output_index})`}
            />
          )}

          {/* <Button
            variant="secondary"
            className="mt-4"
            type="button"
            onClick={handleTestBackwash}
          >
            Testar Retrolavagem
          </Button> */}
        </div>

        {/* Action Buttons */}
        <ModalFooter
          primaryAction={{
            label: "Salvar",
            onClick: handleSave,
          }}
          secondaryAction={{
            label: "Cancelar",
            onClick: handleCancel,
          }}
        />
      </div>
    </Modal>
  );
}

export default PumpDetailModal;
